import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import AppSidebar from "@/components/AppSidebar";
import { 
  MessageSquare,
  Mail,
  FileText,
  ExternalLink,
  Loader2,
  CheckCircle2,
  HelpCircle,
  BookOpen,
  Zap
} from "lucide-react";

const faqs = [
  {
    question: "How does AI lead scoring work?",
    answer: "Our AI analyzes submission data including budget, timeline, decision-maker status, and problem clarity to generate a score from 0-100. Higher scores indicate more qualified leads."
  },
  {
    question: "Can I customize my intake forms?",
    answer: "Yes! Use the Form Builder to add custom fields, change colors, upload your logo, and modify welcome messages to match your brand."
  },
  {
    question: "How do I upgrade my subscription?",
    answer: "Go to Settings > Subscription and click 'Upgrade to Paid' to access premium features like unlimited submissions and custom branding."
  },
  {
    question: "What happens when I reach my submission limit?",
    answer: "On the free trial, you're limited to 20 submissions. Once reached, new submissions will be paused until you upgrade to a paid plan."
  },
  {
    question: "How do I share my intake form?",
    answer: "Copy your form link from the Dashboard or Form Builder and share it with clients via email, website, or social media."
  }
];

export default function SupportPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    subject: "",
    message: ""
  });
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // In a real app, this would send to a support ticket system
      // For now, we'll simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitted(true);
      toast.success("Support request submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex" >
      <AppSidebar user={user} onLogout={() => { logout(); navigate("/"); }} />
      
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground" >
              Support Center
            </h1>
            <p className="text-muted-foreground mt-1">
              Get help with HeyIntake or contact our support team
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Links */}
            <div className="lg:col-span-1 space-y-4">
              <Card className="rounded-2xl border-none shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    Quick Links
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <a 
                    href="/docs" 
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors"
                  >
                    <BookOpen className="w-5 h-5 text-muted-foreground" />
                    <span>Documentation</span>
                    <ExternalLink className="w-4 h-4 ml-auto text-muted-foreground" />
                  </a>
                  <a 
                    href="/faq" 
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors"
                  >
                    <HelpCircle className="w-5 h-5 text-muted-foreground" />
                    <span>FAQ</span>
                    <ExternalLink className="w-4 h-4 ml-auto text-muted-foreground" />
                  </a>
                  <a 
                    href="mailto:hello@heyintake.com" 
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors"
                  >
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <span>Email Support</span>
                    <ExternalLink className="w-4 h-4 ml-auto text-muted-foreground" />
                  </a>
                </CardContent>
              </Card>

              {/* Account Info */}
              <Card className="rounded-2xl border-none shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Your Account</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Email</span>
                    <span className="font-medium">{user?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Business</span>
                    <span className="font-medium">{user?.business_name}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* FAQ Section */}
              <Card className="rounded-2xl border-none shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-primary" />
                    Frequently Asked Questions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="border rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="w-full p-4 text-left flex items-center justify-between hover:bg-muted/50 transition-colors"
                      >
                        <span className="font-medium">{faq.question}</span>
                        <span className="text-muted-foreground">
                          {expandedFaq === index ? "−" : "+"}
                        </span>
                      </button>
                      {expandedFaq === index && (
                        <div className="px-4 pb-4 text-muted-foreground">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Contact Form */}
              <Card className="rounded-2xl border-none shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    Contact Support
                  </CardTitle>
                  <CardDescription>
                    Can't find what you're looking for? Send us a message.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">Request Submitted!</h3>
                      <p className="text-muted-foreground mb-4">
                        We'll get back to you within 24-48 hours.
                      </p>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setSubmitted(false);
                          setFormData({ subject: "", message: "" });
                        }}
                      >
                        Submit Another Request
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject</Label>
                        <Input
                          id="subject"
                          placeholder="Brief description of your issue"
                          value={formData.subject}
                          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                          required
                          data-testid="support-subject-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Message</Label>
                        <Textarea
                          id="message"
                          placeholder="Describe your issue or question in detail..."
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                          rows={5}
                          data-testid="support-message-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full rounded-full"
                        disabled={loading}
                        data-testid="support-submit-btn"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <MessageSquare className="mr-2 h-4 w-4" />
                            Submit Request
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
